/**
 * studioStyles.ts — Fonte ÚNICA e NEUTRA de estilo do Studio
 *
 * Este módulo não depende de DOM, React, Node nem Deno. É a origem conceitual
 * do CSS do Studio: tanto o renderizador do editor (via `studioLayers.css`,
 * que é GERADO a partir daqui) quanto o compilador HTML das Edge Functions
 * consomem estas mesmas funções.
 *
 * Antes existiam duas folhas espelhadas à mão — o que já fez a página publicada
 * sair diferente da tela de criação. Agora há uma só.
 *
 * Para regerar o CSS do editor: `bun scripts/gerar-css-studio.ts`
 * Para conferir sem escrever:   `bun scripts/gerar-css-studio.ts --check`
 *
 * RELEASE: v1.9.0 - 2026-09-11 - Estilos dos blocos de página extraídos para
 *            `pageStyles()`, permitindo que editor e publicação usem a mesma
 *            fonte sem levar os estilos globais de impressão para o painel.
 * RELEASE: v1.8.0 - 2026-09-11 - Faixa de borda a borda (`.st-contained` para as
 *            exceções), player de vídeo com capa e botão de play, e classe
 *            `.st-no-motion` para miniaturas nunca ficarem no quadro invisível.
 * RELEASE: v1.7.0 - 2026-09-11 - Responsividade real: consultas de contêiner
 *            (`@container`) para o documento, tipografia fluida (`clamp`),
 *            colapso de colunas por largura do documento e fim da duplicação
 *            interna da folha.
 * RELEASE: v1.6.0 - 2026-09-11 - Direção de arte por tela e redução automática
 *            de intensidade no celular (motor de composições, Fase 7)
 * RELEASE: v1.5.0 - 2026-09-11 - Recortes, inclinação e formas gerados pelo registro (visual.ts) — Fase 6
 * RELEASE: v1.4.0 - 2026-09-11 - Movimento gerado pelo registro (motion.ts) — Fase 5
 * RELEASE: v1.3.0 - 2026-09-11 - Efeitos visuais gerados pelo registro (effects.ts) — Fase 4
 * RELEASE: v1.2.0 - 2026-09-11 - Camadas e posicionamento governado (classes st-layer, st-pos, st-z e st-an) — Fase 3
 * RELEASE: v1.4.0 - 2026-09-11 - Grades passam a responder à largura do documento;
 *            cartões, planos, FAQ e formulários usam superfícies e contornos
 *            adaptáveis ao tema claro/escuro, sem linhas brancas rígidas.
 * RELEASE: v1.1.0 - 2026-09-11 - Grade interna de 12 colunas (.st-grid/.st-gc*) — Fase 2
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (motor de composições, Fase 1)
 */

// Registro de efeitos visuais (Fase 4): o CSS é emitido pelo próprio catálogo.
import { effectStyles } from './effects.ts';
// Registro de movimento (Fase 5): quadros, regras, atrasos e durações do catálogo.
import { motionStyles } from './motion.ts';
// Registro visual (Fase 6): recortes (máscaras), inclinação e formas decorativas.
import { visualStyles } from './effects.ts';
// Direção de arte por tela e redução automática no celular (Fase 7).
import { screenStyles } from './effects.ts';
// Fundos decorativos e acabamentos de borda (biblioteca própria, Fase 1).
import { decorStyles } from './decor.ts';
import { interactiveStyles } from './interactive.ts';

/**
 * Aparência dos blocos de página.
 *
 * Esta parte é neutra e não contém seletores globais de `body`, tabela ou
 * títulos. Por isso pode ser carregada com segurança dentro do editor.
 */
export function pageStyles(): string {
  return `
      /* Blocos de página pública (landing) */
      .page-hero { padding: 56px 24px; border-radius: 16px; }
      .page-hero h1 { font-size: 30px; margin: 0 0 10px 0; line-height: 1.2; }
      .page-hero p { font-size: 16px; margin: 0; opacity: .85; }
      .page-hero img { max-width: 100%; border-radius: 12px; margin-top: 28px; }
      .page-cta { display: inline-block; margin-top: 26px; padding: 12px 24px; border-radius: 12px; background: var(--st-accent, #2563eb); color: #ffffff; font-weight: 600; text-decoration: none; }
      .page-section { padding: 36px 0; }
      .page-section h2 { font-size: 22px; text-align: center; margin: 0 0 22px 0; }
      .page-grid { display: grid; gap: 20px; }
      .page-card { border: 1px solid color-mix(in srgb, currentColor 12%, transparent); border-radius: 12px; padding: 18px; background: color-mix(in srgb, currentColor 4%, transparent); }
      .page-card h3 { font-size: 16px; margin: 0 0 6px 0; }
      .page-card p { font-size: 14px; color: inherit; opacity: .74; margin: 0; }
      .page-quote { font-style: italic; font-size: 14px; margin: 0; }
      .page-quote-author { font-size: 14px; font-weight: 600; margin-top: 10px; }
      .page-form { border: 1px solid color-mix(in srgb, currentColor 12%, transparent); border-radius: 16px; padding: 24px; background: color-mix(in srgb, currentColor 3%, transparent); }
      .page-form label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
      .page-form .field { margin-bottom: 14px; }
      .page-form input[type="text"], .page-form input[type="email"], .page-form input[type="number"], .page-form select, .page-form textarea { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid color-mix(in srgb, currentColor 16%, transparent); border-radius: 8px; font-size: 14px; color: inherit; background: color-mix(in srgb, currentColor 5%, transparent); }
      .page-form .consent { display: flex; gap: 8px; align-items: flex-start; font-size: 12px; color: inherit; opacity: .74; }
      .page-form button { width: 100%; padding: 12px 16px; border: none; border-radius: 12px; background: var(--st-accent, #2563eb); color: #ffffff; font-size: 14px; font-weight: 600; cursor: pointer; }
      .page-honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; }
      .page-image { display: block; max-width: 100%; margin: 0 auto; }
      .page-button { display: inline-block; padding: 12px 24px; border-radius: 12px; text-decoration: none; font-weight: 600; }

      /* Blocos de seção genéricos (construtor de blocos prontos) */
      .page-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; color: inherit; opacity: .7; margin: 0; }
      .page-lead { font-size: 15px; color: inherit; opacity: .78; margin: 6px auto 0 auto; max-width: 640px; }
      .page-split { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px; align-items: center; }
      .page-split-img { width: 100%; border-radius: 16px; object-fit: cover; }
      .page-stat-value { font-size: 30px; font-weight: 700; margin: 0; }
      .page-stat-label { font-size: 13px; opacity: .75; margin: 4px 0 0 0; }
      .page-logos { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 32px; }
      .page-plan { display: flex; flex-direction: column; padding: 26px; border-radius: 16px; }
      .page-plan-featured { border-color: color-mix(in srgb, var(--st-accent, #2563eb) 72%, transparent); box-shadow: 0 18px 44px -30px var(--st-accent, #2563eb); background: color-mix(in srgb, var(--st-accent, #2563eb) 7%, transparent); }
      .page-price { font-size: 28px; font-weight: 700; margin: 10px 0 0 0; }
      .page-plan-list { list-style: none; padding: 0; margin: 14px 0 0 0; font-size: 14px; }
      .page-plan-list li { position: relative; padding: 6px 0 6px 20px; opacity: .82; }
      .page-plan-list li::before { content: "✓"; position: absolute; left: 0; color: var(--st-accent, #2563eb); font-weight: 700; }
      .page-step { display: flex; gap: 12px; align-items: flex-start; }
      .page-step-number { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 999px; background: var(--st-accent, #2563eb); color: #fff; font-size: 12px; font-weight: 700; flex: 0 0 26px; }
      .page-avatar { width: 92px; height: 92px; border-radius: 999px; object-fit: cover; }
      .page-avatar-empty { background: #e5e7eb; display: inline-block; }
      .page-deadline { border-radius: 12px; padding: 14px 18px; text-align: center; }
      .page-nav { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 20px; border-radius: 12px; color: inherit; }
      .page-nav-brand { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 14px; }
      .page-nav-links { display: flex; flex-wrap: wrap; align-items: center; gap: 20px; font-size: 14px; }
      .page-nav-links a { color: inherit; text-decoration: none; opacity: .85; }
      .page-nav-cta { padding: 8px 16px; border-radius: 10px; background: var(--st-accent, #2563eb); color: #fff !important; font-weight: 600; font-size: 13px; }
      .page-footer { border-radius: 16px; padding: 28px 24px; }
      .page-footer-links { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 14px; font-size: 14px; }
      .page-footer-links a { color: inherit; text-decoration: none; opacity: .85; }
      .page-banner { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 10px; padding: 10px 16px; border-radius: 10px; font-size: 13px; text-align: center; }
      .page-banner a { color: inherit; font-weight: 600; }

      /* No editor o documento é uma janela própria: elementos flutuantes ficam
         presos a essa janela, sem escapar para fora do canvas. */
      .studio-editor-document { position: relative; }
      .studio-editor-document .st-ib-float { position: absolute; }

      @container doc (max-width: 640px) {
        .page-split { grid-template-columns: minmax(0, 1fr); }
        .page-grid { grid-template-columns: minmax(0, 1fr) !important; }
      }
  `;
}

export function baseStyles(): string {
  return `
      html { -webkit-print-color-adjust: exact; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #1f2937; margin: 0; }
      .muted { color: #6b7280; font-size: 9pt; margin-top: 2px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 18px; }
      .brand { font-size: 16pt; font-weight: bold; color: #111827; }
      .logo { max-height: 56px; max-width: 190px; }
      .doc-meta { text-align: right; font-size: 9.5pt; color: #4b5563; }
      .doc-title { font-size: 15pt; font-weight: bold; color: #2563eb; }
      .cards { display: flex; gap: 12px; margin-bottom: 18px; }
      .card { flex: 1; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 12px; }
      .card h4 { margin: 0 0 6px 0; font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #6b7280; }
      h3.section { font-size: 11.5pt; margin: 18px 0 8px 0; color: #111827; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #f3f4f6; font-size: 9pt; text-transform: uppercase; letter-spacing: .4px; color: #374151; }
      th, td { border: 1px solid #e5e7eb; padding: 7px 8px; text-align: left; vertical-align: top; }
      td.right, th.right { text-align: right; }
      td.center, th.center { text-align: center; }
      .totals { margin-top: 12px; width: 46%; margin-left: auto; }
      .totals td { border: none; padding: 4px 0; }
      .totals .grand { border-top: 2px solid #2563eb; font-size: 12.5pt; font-weight: bold; color: #111827; padding-top: 8px; }
      .conditions { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; font-size: 10pt; }
      .footer-note { margin-top: 22px; font-size: 8.5pt; color: #9ca3af; text-align: center; }
      .block-text { font-size: 10.5pt; margin: 0 0 10px 0; }
      .block-divider { border: none; border-top: 1px solid #e5e7eb; margin: 14px 0; }

${pageStyles()}
${layerStyles()}
  `;
}

/**
 * Estilos das camadas, dos primitivos e do movimento.
 *
 * Fonte única: `src/lib/studio/render/web/studioLayers.css` é GERADO a partir
 * desta função. Não edite aquele arquivo à mão.
 */
export function layerStyles(): string {
  return `
      .st-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); width: 100%; }
      .st-grid > .st-gc { min-width: 0; }
      .st-grid > .st-gc-1 { grid-column: span 1 / span 1; }
      .st-grid > .st-gc-2 { grid-column: span 2 / span 2; }
      .st-grid > .st-gc-3 { grid-column: span 3 / span 3; }
      .st-grid > .st-gc-4 { grid-column: span 4 / span 4; }
      .st-grid > .st-gc-5 { grid-column: span 5 / span 5; }
      .st-grid > .st-gc-6 { grid-column: span 6 / span 6; }
      .st-grid > .st-gc-7 { grid-column: span 7 / span 7; }
      .st-grid > .st-gc-8 { grid-column: span 8 / span 8; }
      .st-grid > .st-gc-9 { grid-column: span 9 / span 9; }
      .st-grid > .st-gc-10 { grid-column: span 10 / span 10; }
      .st-grid > .st-gc-11 { grid-column: span 11 / span 11; }
      .st-grid > .st-gc-12 { grid-column: span 12 / span 12; }
      @container doc (max-width: 900px) {
      .st-grid > .st-gc-t-1 { grid-column: span 1 / span 1; }
      .st-grid > .st-gc-t-2 { grid-column: span 2 / span 2; }
      .st-grid > .st-gc-t-3 { grid-column: span 3 / span 3; }
      .st-grid > .st-gc-t-4 { grid-column: span 4 / span 4; }
      .st-grid > .st-gc-t-5 { grid-column: span 5 / span 5; }
      .st-grid > .st-gc-t-6 { grid-column: span 6 / span 6; }
      .st-grid > .st-gc-t-7 { grid-column: span 7 / span 7; }
      .st-grid > .st-gc-t-8 { grid-column: span 8 / span 8; }
      .st-grid > .st-gc-t-9 { grid-column: span 9 / span 9; }
      .st-grid > .st-gc-t-10 { grid-column: span 10 / span 10; }
      .st-grid > .st-gc-t-11 { grid-column: span 11 / span 11; }
      .st-grid > .st-gc-t-12 { grid-column: span 12 / span 12; }
      }
      @container doc (max-width: 640px) {
        .st-grid > .st-gc { grid-column: span 12 / span 12; }
      .st-grid > .st-gc-m-1 { grid-column: span 1 / span 1; }
      .st-grid > .st-gc-m-2 { grid-column: span 2 / span 2; }
      .st-grid > .st-gc-m-3 { grid-column: span 3 / span 3; }
      .st-grid > .st-gc-m-4 { grid-column: span 4 / span 4; }
      .st-grid > .st-gc-m-5 { grid-column: span 5 / span 5; }
      .st-grid > .st-gc-m-6 { grid-column: span 6 / span 6; }
      .st-grid > .st-gc-m-7 { grid-column: span 7 / span 7; }
      .st-grid > .st-gc-m-8 { grid-column: span 8 / span 8; }
      .st-grid > .st-gc-m-9 { grid-column: span 9 / span 9; }
      .st-grid > .st-gc-m-10 { grid-column: span 10 / span 10; }
      .st-grid > .st-gc-m-11 { grid-column: span 11 / span 11; }
      .st-grid > .st-gc-m-12 { grid-column: span 12 / span 12; }
      }

      /* ---------------------------------------------------------------------
         Camadas e posicionamento governado (Fase 3)

         O elemento só ganha posicionamento quando o usuário escolhe. Ancorar
         (absolute) exige que a seção esteja com "camadas livres" ligadas
         (.st-layers), e no celular tudo volta ao fluxo, salvo .st-keep-mobile.
         Deslocamento, giro, escala e opacidade vêm de variáveis por tela.
      --------------------------------------------------------------------- */
      .st-layers { position: relative; }
      .st-layer {
        --st-x: var(--st-x-d, 0px);
        --st-y: var(--st-y-d, 0px);
        --st-rot: var(--st-rot-d, 0deg);
        --st-scale: var(--st-scale-d, 1);
        --st-op: var(--st-op-d, 1);
        --st-tx: 0%;
        --st-ty: 0%;
        transform: translate(var(--st-tx), var(--st-ty)) translate(var(--st-x), var(--st-y)) rotate(var(--st-rot)) scale(var(--st-scale));
        opacity: var(--st-op);
      }
      .st-pos-relative { position: relative; }
      .st-pos-sticky { position: sticky; top: var(--st-sticky, 16px); }
      .st-layers > .st-pos-absolute { position: absolute; }
      .st-layers > .st-pos-fixed { position: fixed; }
      .st-z-behind { z-index: 0; }
      .st-z-base { z-index: 1; }
      .st-z-content { z-index: 2; }
      .st-z-overlay { z-index: 3; }
      .st-z-floating { z-index: 4; }
      .st-z-top { z-index: 5; }
      .st-an-top-left { top: 0; left: 0; }
      .st-an-top-center { top: 0; left: 50%; --st-tx: -50%; }
      .st-an-top-right { top: 0; right: 0; }
      .st-an-middle-left { top: 50%; left: 0; --st-ty: -50%; }
      .st-an-center { top: 50%; left: 50%; --st-tx: -50%; --st-ty: -50%; }
      .st-an-middle-right { top: 50%; right: 0; --st-ty: -50%; }
      .st-an-bottom-left { bottom: 0; left: 0; }
      .st-an-bottom-center { bottom: 0; left: 50%; --st-tx: -50%; }
      .st-an-bottom-right { bottom: 0; right: 0; }
      @media (max-width: 900px) {
        .st-layer {
          --st-x: var(--st-x-t, var(--st-x-d, 0px));
          --st-y: var(--st-y-t, var(--st-y-d, 0px));
          --st-rot: var(--st-rot-t, var(--st-rot-d, 0deg));
          --st-scale: var(--st-scale-t, var(--st-scale-d, 1));
          --st-op: var(--st-op-t, var(--st-op-d, 1));
        }
      }
      @media (max-width: 640px) {
        .st-layer {
          --st-x: var(--st-x-m, var(--st-x-t, var(--st-x-d, 0px)));
          --st-y: var(--st-y-m, var(--st-y-t, var(--st-y-d, 0px)));
          --st-rot: var(--st-rot-m, var(--st-rot-t, var(--st-rot-d, 0deg)));
          --st-scale: var(--st-scale-m, var(--st-scale-t, var(--st-scale-d, 1)));
          --st-op: var(--st-op-m, var(--st-op-t, var(--st-op-d, 1)));
        }
        .st-layers > .st-pos-absolute:not(.st-keep-mobile),
        .st-layers > .st-pos-fixed:not(.st-keep-mobile) {
          position: relative;
          top: auto; left: auto; right: auto; bottom: auto;
          --st-tx: 0%; --st-ty: 0%;
        }
      }
      @media print {
        .st-layers > .st-pos-absolute, .st-layers > .st-pos-fixed { position: relative; top: auto; left: auto; right: auto; bottom: auto; --st-tx: 0%; --st-ty: 0%; }
        .st-pos-sticky { position: relative; }
      }

      .st-sec { position: relative; overflow: hidden; isolation: isolate; width: 100%; display: block; }
      /* Faixa "com margem lateral": a seção deixa de encostar nas bordas e volta
         a respeitar a largura do miolo. É o que vídeo, formulário e contato
         usam. Sem esta classe, a faixa vai de borda a borda. */
      .st-contained { max-width: var(--studio-page-width, 1120px); margin-left: auto; margin-right: auto; }
      /* Blocos antigos de página (page.*) nunca foram desenhados para encostar
         nas bordas: continuam centralizados dentro do miolo. */
      .studio-doc > .studio-legacy, .studio-page > .studio-legacy { max-width: var(--studio-page-width, 1120px); margin-left: auto; margin-right: auto; padding-left: 24px; padding-right: 24px; }

      .st-sec > .st-bg, .st-sec > .st-ov, .st-sec > .st-pat, .st-sec > .st-glow { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
      .st-bg { background-size: cover; background-position: center; }
      .st-ov-dark { background: rgba(9,12,20,.55); }
      .st-ov-darker { background: rgba(6,8,14,.75); }
      .st-ov-light { background: rgba(255,255,255,.72); }
      .st-ov-bottom { background: linear-gradient(to top, rgba(6,8,14,.88), rgba(6,8,14,.1) 65%); }
      .st-ov-left { background: linear-gradient(to right, rgba(6,8,14,.9), rgba(6,8,14,0) 70%); }
      .st-gr-mesh-dark { background: radial-gradient(900px 500px at 12% 8%, rgba(56,89,255,.35), transparent 60%), radial-gradient(700px 460px at 88% 18%, rgba(168,63,255,.28), transparent 62%), radial-gradient(760px 520px at 60% 100%, rgba(0,190,190,.22), transparent 60%), #070b16; }
      .st-gr-mesh-brand { background: radial-gradient(760px 480px at 15% 15%, rgba(37,99,235,.28), transparent 62%), radial-gradient(680px 420px at 85% 10%, rgba(14,165,233,.22), transparent 60%), #f6f8fc; }
      .st-gr-sunset { background: linear-gradient(135deg, #2b1055 0%, #7c3aed 45%, #f97316 100%); }
      .st-gr-aurora { background: linear-gradient(120deg, #041b2d 0%, #064663 45%, #04a777 100%); }
      .st-gr-soft { background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%); }
      .st-pat-grid { background-image: linear-gradient(rgba(148,163,184,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.18) 1px, transparent 1px); background-size: 44px 44px; }
      .st-pat-dots { background-image: radial-gradient(rgba(148,163,184,.3) 1.2px, transparent 1.2px); background-size: 22px 22px; }
      .st-pat-rays { background: conic-gradient(from 210deg at 50% -10%, rgba(255,255,255,.14), transparent 25%, rgba(255,255,255,.1) 45%, transparent 70%); }
      .st-glow-top-right { background: radial-gradient(520px 320px at 88% 6%, rgba(99,102,241,.45), transparent 70%); }
      .st-glow-center { background: radial-gradient(620px 380px at 50% 45%, rgba(56,189,248,.32), transparent 70%); }
      .st-glow-bottom-left { background: radial-gradient(560px 340px at 8% 96%, rgba(236,72,153,.32), transparent 70%); }
      .st-inner { position: relative; z-index: 1; margin: 0 auto; width: 100%; padding-left: 24px; padding-right: 24px; display: grid; gap: 40px; align-items: center; }
      .st-pad-sm { padding-top: 32px; padding-bottom: 32px; }
      .st-pad-md { padding-top: 56px; padding-bottom: 56px; }
      .st-pad-lg { padding-top: 88px; padding-bottom: 88px; }
      .st-pad-xl { padding-top: 128px; padding-bottom: 128px; }
      .st-h-md { min-height: 420px; }
      .st-h-lg { min-height: 560px; }
      .st-h-screen { min-height: 88vh; }
      .st-lay-split { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .st-lay-split-reverse { grid-template-columns: repeat(2, minmax(0,1fr)); direction: rtl; }
      .st-lay-split-reverse > * { direction: ltr; }
      .st-lay-split-wide-left { grid-template-columns: 1.35fr 1fr; }
      .st-lay-bento { grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
      .st-lay-bento > *:first-child { grid-column: span 2; }
      .st-al-left { text-align: left; }
      .st-al-center { text-align: center; }
      .st-al-right { text-align: right; }
      .st-r-none { border-radius: 0; }
      .st-r-sm { border-radius: 8px; }
      .st-r-lg { border-radius: 16px; }
      .st-r-xl { border-radius: 26px; }
      .st-r-full { border-radius: 999px; }
      .st-sh-none { box-shadow: none; }
      .st-sh-sm { box-shadow: 0 1px 2px rgba(15,23,42,.08); }
      .st-sh-md { box-shadow: 0 12px 30px -12px rgba(15,23,42,.28); }
      .st-sh-lg { box-shadow: 0 34px 70px -24px rgba(15,23,42,.45); }
      .st-sh-glow { box-shadow: 0 0 0 1px rgba(255,255,255,.08), 0 30px 90px -20px rgba(59,130,246,.55); }
      .st-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px; font-size: 12.5px; font-weight: 600; line-height: 1.2; }
      .st-badge-soft { background: rgba(37,99,235,.1); color: #1d4ed8; }
      .st-badge-outline { border: 1px solid currentColor; opacity: .9; }
      .st-badge-solid { background: #111827; color: #fff; }
      .st-badge-glass { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.22); }
      .st-title { margin: 0; font-family: var(--st-heading-font, inherit); font-weight: var(--st-heading-weight, 700); text-transform: var(--st-heading-transform, none); letter-spacing: var(--st-heading-tracking, -.02em); line-height: 1.08; }
      .st-title-md { font-size: 28px; }
      .st-title-lg { font-size: 38px; }
      .st-title-xl { font-size: 50px; }
      .st-title-display { font-size: 66px; letter-spacing: -.035em; }
      .st-title-hero { font-size: 84px; letter-spacing: -.04em; line-height: .98; }
      .st-p { margin: 14px 0 0 0; line-height: 1.65; opacity: .88; }
      .st-p-sm { font-size: 14px; }
      .st-p-md { font-size: 16.5px; }
      .st-p-lg { font-size: 19px; }
      .st-btns { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 26px; }
      .st-btns-center { justify-content: center; }
      .st-btns-right { justify-content: flex-end; }
      .st-btn { display: inline-flex; align-items: center; justify-content: center; border-radius: 12px; font-weight: 600; text-decoration: none; }
      .st-btn-sm { height: 40px; padding: 0 18px; font-size: 13.5px; }
      .st-btn-md { height: 48px; padding: 0 24px; font-size: 14.5px; }
      .st-btn-lg { height: 56px; padding: 0 32px; font-size: 16px; }
      .st-btn-solid { background: #111827; color: #fff; }
      .st-btn-outline { border: 1px solid currentColor; }
      .st-btn-ghost { opacity: .85; }
      .st-btn-glass { background: rgba(255,255,255,.14); border: 1px solid rgba(255,255,255,.24); }
      .st-img { display: block; width: 100%; height: auto; object-fit: cover; }
      .st-ratio-square { aspect-ratio: 1 / 1; }
      .st-ratio-landscape { aspect-ratio: 4 / 3; }
      .st-ratio-wide { aspect-ratio: 16 / 9; }
      .st-ratio-portrait { aspect-ratio: 3 / 4; }
      .st-card { padding: 20px 22px; }
      .st-card-solid { background: #fff; color: #0f172a; }
      .st-card-outline { border: 1px solid rgba(148,163,184,.35); }
      .st-card-glass { background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2); }
      .st-card-icon { font-size: 20px; line-height: 1; }
      .st-card-title { margin: 10px 0 0 0; font-size: 16px; font-weight: 650; }
      .st-card-text { margin: 6px 0 0 0; font-size: 14px; opacity: .8; line-height: 1.55; }
      .st-avatars { display: flex; align-items: center; gap: 14px; margin-top: 24px; }
      .st-avatars-center { justify-content: center; }
      .st-avatars-right { justify-content: flex-end; }
      .st-avatars-row { display: flex; }
      .st-avatars-row img { width: 38px; height: 38px; border-radius: 999px; object-fit: cover; border: 2px solid rgba(255,255,255,.9); margin-left: -10px; }
      .st-avatars-row img:first-child { margin-left: 0; }
      .st-avatars-text { font-size: 13.5px; font-weight: 600; }
      .st-avatars-note { font-size: 12.5px; opacity: .72; margin-top: 2px; }

      /* ------------------------------------------------- Vocabulário editorial */
      /* Contêiner vertical (layout.stack): agrupa blocos dentro de uma coluna. */
      .st-stack { display: flex; flex-direction: column; min-width: 0; }
      .st-stack-gap-none { gap: 0; }
      .st-stack-gap-xs { gap: 6px; }
      .st-stack-gap-sm { gap: 12px; }
      .st-stack-gap-md { gap: 20px; }
      .st-stack-gap-lg { gap: 34px; }
      .st-stack-al-start { align-items: flex-start; }
      .st-stack-al-center { align-items: center; text-align: center; }
      .st-stack-al-end { align-items: flex-end; text-align: right; }
      .st-stack-al-stretch { align-items: stretch; }
      .st-stack-jt-start { justify-content: flex-start; }
      .st-stack-jt-center { justify-content: center; }
      .st-stack-jt-end { justify-content: flex-end; }
      .st-stack-jt-between { justify-content: space-between; height: 100%; }
      .st-stack-pad-sm { padding: 16px; }
      .st-stack-pad-md { padding: 26px; }
      .st-stack-pad-lg { padding: 40px; }
      .st-stack-bd-soft { border: 1px solid rgba(148,163,184,.28); }
      .st-stack-bd-accent { border: 1px solid var(--st-accent, #2563eb); }
      .st-stack-bd-left { border-left: 3px solid var(--st-accent, #2563eb); padding-left: 20px; }
      .st-stack-sticky { position: sticky; top: 24px; }

      /* Selo editorial: traço curto + texto pequeno em caixa alta. */
      .st-badge-line { padding: 0; border-radius: 0; background: none; text-transform: uppercase; letter-spacing: .16em; font-size: 11.5px; color: var(--st-accent, #2563eb); }
      .st-badge-rule { display: inline-block; width: 28px; height: 2px; background: currentColor; }

      /* Título: caixa e entrelinha por bloco. */
      .st-tt-none { text-transform: none; }
      .st-tt-uppercase { text-transform: uppercase; }
      .st-lead-tight { line-height: .98; }
      .st-lead-normal { line-height: 1.08; }
      .st-lead-loose { line-height: 1.25; }

      /* Botões: cantos, caixa alta e seta. */
      .st-btn-shape-square { border-radius: 2px; }
      .st-btn-shape-pill { border-radius: 999px; }
      .st-btn-caps { text-transform: uppercase; letter-spacing: .1em; font-size: 12.5px; }
      .st-btn-arrow { margin-left: 10px; }

      /* Cartão editorial. */
      .st-card-editorial { background: rgba(255,255,255,.03); border: 1px solid rgba(148,163,184,.22); }
      .st-card-badge { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 12px; border: 1px solid rgba(148,163,184,.3); color: var(--st-accent, #2563eb); }
      .st-icon { display: block; }
      .st-card-index { display: block; font-size: 12px; font-weight: 700; letter-spacing: .16em; opacity: .55; }
      .st-card-eyebrow { margin: 12px 0 0 0; font-size: 11.5px; text-transform: uppercase; letter-spacing: .14em; opacity: .6; }
      .st-card-hl { border-color: var(--st-accent, #2563eb); box-shadow: 0 0 0 1px var(--st-accent, #2563eb) inset; }
      .st-card-h-md { min-height: 220px; }
      .st-card-h-lg { min-height: 300px; }
      .st-card-hover { transition: transform .25s ease, border-color .25s ease; }
      .st-card-hover:hover { transform: translateY(-4px); border-color: var(--st-accent, #2563eb); }

      /* Avatares sem foto e contador. */
      .st-avatar-ini { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 999px; margin-left: -10px; border: 2px solid rgba(255,255,255,.9); background: rgba(148,163,184,.35); font-size: 12.5px; font-weight: 700; }
      .st-avatars-row > :first-child { margin-left: 0; }
      .st-avatar-more { background: var(--st-accent, #2563eb); color: #fff; }
      .st-avatars-rating { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--st-accent, #2563eb); }
      .st-avatars-rating strong { color: inherit; }

      .st-stat-value { margin: 0; font-size: 38px; font-weight: 700; letter-spacing: -.02em; }
      .st-stat-label { margin: 4px 0 0 0; font-size: 13.5px; opacity: .75; }
      .st-logos { display: flex; flex-wrap: wrap; align-items: center; gap: 26px; margin-top: 22px; }
      .st-logos-center { justify-content: center; }
      .st-logos img { height: 26px; width: auto; object-fit: contain; }
      .st-logos-gray img { filter: grayscale(1); opacity: .65; }
      .st-logos-note { width: 100%; font-size: 11.5px; text-transform: uppercase; letter-spacing: .14em; opacity: .6; }
      .st-dev { width: 100%; }
      .st-dev-browser { border-radius: 14px; overflow: hidden; background: #0f172a; }
      .st-dev-bar { display: flex; gap: 6px; padding: 10px 12px; background: rgba(255,255,255,.08); }
      .st-dev-bar span { width: 9px; height: 9px; border-radius: 999px; background: rgba(255,255,255,.35); }
      .st-dev-phone { border-radius: 34px; overflow: hidden; background: #0f172a; padding: 10px; max-width: 300px; }
      .st-dev-phone img { border-radius: 26px; }
      .st-dev-laptop { border-radius: 14px 14px 4px 4px; overflow: hidden; background: #0f172a; padding: 10px 10px 0 10px; }
      .st-dev-base { height: 12px; border-radius: 0 0 14px 14px; background: #1e293b; }
      .st-video { width: 100%; aspect-ratio: 16 / 9; border: 0; display: block; }

      /* ------------------------------------------------------------------
         Vídeo (player com capa e botão de play)
         O quadro só carrega o serviço depois do clique: a página abre leve e
         a capa aparece na hora, sem retângulo preto.
      ------------------------------------------------------------------ */
      .st-vid { position: relative; width: 100%; overflow: hidden; background: #0b1220; }
      .st-vid-16x9 { aspect-ratio: 16 / 9; }
      .st-vid-4x3 { aspect-ratio: 4 / 3; }
      .st-vid-1x1 { aspect-ratio: 1 / 1; }
      .st-vid-9x16 { aspect-ratio: 9 / 16; max-width: 380px; margin-left: auto; margin-right: auto; }
      .st-vid > iframe, .st-vid > video, .st-vid > .st-vid-poster { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
      .st-vid-poster { display: block; object-fit: cover; }
      .st-vid-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(6,8,14,.15), rgba(6,8,14,.55)); }
      .st-vid-play { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 12px; flex-direction: column; border: 0; background: transparent; color: #ffffff; cursor: pointer; font: inherit; padding: 0; }
      .st-vid-play::before { content: ''; width: 78px; height: 78px; border-radius: 999px; background: rgba(255,255,255,.16); backdrop-filter: blur(6px); box-shadow: 0 18px 50px -18px rgba(0,0,0,.8), inset 0 0 0 1px rgba(255,255,255,.5); background-image: linear-gradient(rgba(255,255,255,.08), rgba(255,255,255,0)); }
      .st-vid-play::after { content: ''; position: absolute; top: calc(50% - 13px); left: calc(50% - 8px); border-style: solid; border-width: 13px 0 13px 22px; border-color: transparent transparent transparent #ffffff; }
      .st-vid-label { position: absolute; bottom: 18px; left: 0; right: 0; text-align: center; font-size: 13px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: rgba(255,255,255,.86); }
      .st-vid:hover .st-vid-play::before { background: rgba(255,255,255,.26); }
      .st-vid-empty { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; padding: 24px; color: rgba(255,255,255,.82); font-size: 13.5px; }

      /* Miniatura e captura: nada anima e nada fica no primeiro quadro (que é
         justamente o quadro invisível). Sem isto, o cartão da biblioteca mostra
         um bloco branco com o texto apagado. */
      .st-no-motion, .st-no-motion * { animation: none !important; transition: none !important; opacity: 1 !important; filter: none !important; animation-timeline: auto !important; }
${motionStyles()}
${responsiveStyles()}
${effectStyles()}
${visualStyles()}
${screenStyles()}
${decorStyles()}
${interactiveStyles()}
`;
}

/**
 * Responsividade real do documento (v1.7.0).
 *
 * Antes tudo dependia da LARGURA DA JANELA. Só que o documento também é
 * desenhado dentro de espaços estreitos — o modo celular do editor e as
 * miniaturas da biblioteca — onde a janela continua larga. O resultado eram
 * três colunas espremidas e texto estourando a caixa.
 *
 * Agora o documento é um CONTÊINER (`container-type: inline-size`) e as regras
 * respondem à largura do próprio documento (`@container`), valendo ao mesmo
 * tempo para o celular de verdade, para a prévia e para a miniatura. A
 * tipografia usa `clamp` com unidade de contêiner (`cqi`), então título e
 * valor encolhem sozinhos em vez de quebrar letra a letra.
 */
export function responsiveStyles(): string {
  return `
      /* Raiz do documento: quem mede a largura para todas as regras abaixo.
         O box-sizing garante que o recuo lateral entre no cálculo — sem ele,
         a faixa ficava mais larga que a tela do celular. */
      .studio-doc { container-type: inline-size; container-name: doc; }
      .studio-doc, .studio-doc *, .studio-doc *::before, .studio-doc *::after { box-sizing: border-box; }

      /* --------------------------------------------- Tipografia fluida */
      .st-title-md { font-size: clamp(21px, 4.6cqi, 28px); }
      .st-title-lg { font-size: clamp(24px, 6cqi, 38px); }
      .st-title-xl { font-size: clamp(27px, 7.4cqi, 50px); }
      .st-title-display { font-size: clamp(30px, 9.4cqi, 66px); }
      .st-title-hero { font-size: clamp(32px, 11cqi, 84px); }
      .st-p-sm { font-size: clamp(13px, 1.6cqi, 14px); }
      .st-p-md { font-size: clamp(14px, 2cqi, 16.5px); }
      .st-p-lg { font-size: clamp(15px, 2.4cqi, 19px); }
      .st-stat-value { font-size: clamp(24px, 5.4cqi, 38px); }
      .page-price { font-size: clamp(21px, 5cqi, 28px); }
      .page-hero h1 { font-size: clamp(23px, 5.6cqi, 30px); }
      .page-stat-value { font-size: clamp(22px, 5cqi, 30px); }
      .st-title, .st-p, .page-card h3, .page-card p, .page-plan, .page-price,
      .st-card-title, .st-card-text, .st-stat-value, .st-btn {
        overflow-wrap: anywhere; word-break: normal; hyphens: auto;
      }

      /* ------------------------------- Documento estreito (tablet, 900px) */
      @container doc (max-width: 900px) {
        .st-lay-bento { grid-template-columns: repeat(2, minmax(0,1fr)); }
        .st-inner { gap: 32px; }
        /* Grades com número de colunas escolhido pelo usuário (recursos,
           preços, equipe, galeria...) caem para duas colunas. */
        .st-grid-auto { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
      }

      /* ------------------------------- Documento estreito (celular, 640px) */
      @container doc (max-width: 640px) {
        .st-grid-auto { grid-template-columns: minmax(0,1fr) !important; gap: 14px; }
        .st-inner { grid-template-columns: minmax(0,1fr) !important; gap: 26px; padding-left: 18px; padding-right: 18px; }
        .st-lay-split-reverse { direction: ltr; }
        .st-lay-bento { grid-template-columns: minmax(0,1fr); }
        .st-lay-bento > *:first-child { grid-column: span 1; }
        .st-pad-md { padding-top: 40px; padding-bottom: 40px; }
        .st-pad-lg { padding-top: 52px; padding-bottom: 52px; }
        .st-pad-xl { padding-top: 64px; padding-bottom: 64px; }
        .st-h-md, .st-h-lg, .st-h-screen { min-height: 0; }
        .st-grid > .st-gc { grid-column: span 12 / span 12; }
        .st-btns { margin-top: 20px; gap: 10px; }
        .st-btns > .st-btn { width: 100%; }
        .st-btn-lg { height: 50px; font-size: 15px; }
        .st-card { padding: 16px 18px; }
        .st-stack-sticky { position: static; }
        .st-stack-pad-lg { padding: 22px; }
        .st-card-h-md, .st-card-h-lg { min-height: 0; }
        .st-logos { gap: 18px; }
        .st-logos img { height: 20px; }
        .st-avatars { gap: 10px; }
        .st-dev-phone { max-width: 100%; }
        .page-grid { grid-template-columns: minmax(0,1fr) !important; gap: 14px; }
        .page-split { grid-template-columns: minmax(0, 1fr) !important; gap: 20px; }
        .page-section { padding: 24px 0; }
        .page-hero { padding: 36px 18px; }
        .page-nav, .page-footer, .page-form { padding: 16px 18px; }
        .page-nav-links { gap: 12px; font-size: 13px; }
        .page-logos { gap: 18px; }
        .page-avatar { width: 68px; height: 68px; }
        .page-cta, .page-button { display: block; text-align: center; }
        .st-layers > .st-pos-absolute:not(.st-keep-mobile),
        .st-layers > .st-pos-fixed:not(.st-keep-mobile) {
          position: relative;
          top: auto; left: auto; right: auto; bottom: auto;
          --st-tx: 0%; --st-ty: 0%;
        }
      }

      /* Impressão e PDF: nada de colapso por contêiner estreito. */
      @media print {
        .studio-doc { container-type: normal; }
      }
  `;
}
